import { useState, DragEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { fileToBase64 } from "../utils";
import { whatsapp_agent_doc_upload } from "../constants/apis";

import DragAndDrop from "../components/DragAndDrop";
import { CrossIcon, SpinnerIcon } from "../components/Icons";
import InvalidLinkPage from "./InvalidLinkPage";
import taramLogoFull from "../assets/icons/TaramFullLogo.png";

export default function WhatsAppDocUpload() {
  const [file, setFile] = useState<File>();
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const [searchParams] = useSearchParams();

  const uid = searchParams.get("uid");

  // Handle file selection
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setFile(event.target.files[0]);
      setMessage(null);
    }
  };

  // Handle Drag & Drop
  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (event.dataTransfer.files.length > 0) {
      setFile(event.dataTransfer.files[0]);
      setMessage(null);
    }
  };

  // const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
  //   event.preventDefault();
  // };

  // Handle file upload
  const handleUpload = async () => {
    if (!file) {
      setMessage("Please select a file.");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const base64String = await fileToBase64(file);

      const res = await fetch(whatsapp_agent_doc_upload, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          base64File: base64String,
          uid,
          folder_name: "deal_tracker",
        }),
      });

      const data = await res.json();

      if (data.status === "success") {
        toast.success(`File "${file.name}" uploaded successfully!`);

        setFile(undefined); // Clear file input after upload
      } else {
        toast.error("File upload went wrong please try again");
      }
    } catch (error) {
      toast.error("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  if (!uid) return <InvalidLinkPage />;

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <div className="bg-white shadow-lg rounded-lg p-6 w-96">
        <div className="mb-2">
          <img
            src={taramLogoFull}
            alt="logo"
            width={100}
            height={20}
            className="full_logo"
          />
        </div>

        <div className="space-y-1 mb-4">
          <h2 className="text-gray-900 text-lg font-semibold">
            Upload Your File
          </h2>
          <p className="text-xs text-gray-500">
            Please upload your document in one of the supported formats: PNG,
            JPG, JPEG, or PDF. Make sure your file is clear and within the
            allowed size limit for best results.
          </p>
        </div>

        <DragAndDrop
          id="trade_flow-vendor_file_upload"
          onChange={handleFileChange}
          onDrop={handleDrop}
          file={file}
        />

        {/* Hidden File Input */}
        <input
          type="file"
          id="fileInput"
          className="hidden"
          onChange={handleFileChange}
        />

        {file && (
          <>
            <p className="text-[#344054] text-center text-sm font-medium mt-3 truncate px-2">
              {file.name}
            </p>

            <div className="relative mt-3 flex justify-center">
              {file.type.startsWith("image/") && (
                <img
                  src={URL.createObjectURL(file)}
                  alt={file.name}
                  className="max-h-48 max-w-full rounded-lg shadow-md border border-gray-200"
                />
              )}

              {file.type === "application/pdf" && (
                <embed
                  src={`${URL.createObjectURL(
                    file,
                  )}#toolbar=0&navpanes=0&scrollbar=0`}
                  type="application/pdf"
                  className="w-full h-64 rounded-lg border border-gray-200"
                />
              )}

              <button
                type="button"
                onClick={() => setFile(undefined)}
                className="absolute top-0 right-2 p-1.5 bg-red-600 text-white rounded-full shadow hover:bg-red-700 transition"
                title="Remove file"
              >
                <CrossIcon className="size-4" />
              </button>
            </div>
          </>
        )}

        {/* Message Display */}
        {message && (
          <small className="mt-4 block text-center text-red-400">
            {message}
          </small>
        )}

        {/* Upload Button */}
        <button
          onClick={handleUpload}
          className="w-full bg-custom-blue-medium text-white py-2 mt-4 flex items-center justify-center rounded-md hover:bg-blue-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={uploading}
        >
          {uploading ? (
            <SpinnerIcon className="size-4 text-gray-200 animate-spin fill-blue-600" />
          ) : (
            "Upload"
          )}
        </button>
      </div>
    </div>
  );
}
